/* STENCIL IMPORTS */
import { Component, Element, Host, Prop, State, h } from "@stencil/core";

import { getIconPath, MercuryBundles } from "@genexus/mercury";

import { Locale } from "../../common/locale";
import { getInitialsFromName, limitFullName } from "./helpers";
import {
  ActionMenuItemActionableModel,
  ActionMenuModel,
  ChActionMenuRenderCustomEvent,
  ChPopoverAlign
} from "@genexus/chameleon-controls-library";

const CSS_BUNDLES: MercuryBundles = [
  // TODO: review if "utils/form--full" is required.
  "resets/box-sizing",
  "components/tooltip",
  "components/switch",
  "components/icon",
  "components/dropdown",
  "utils/layout",
  "utils/typography",
  "utils/spacing",
  "chameleon/scrollbar"
];

const CHEVRON_DOWN_ICON = getIconPath({
  category: "navigation",
  name: "chevron-down",
  colorType: "interactive"
});

const MY_ACCOUNT_ID = "my-account";
const CHANGE_TEAM_ID = "change-team";
const SIGN_OUT_ID = "sign-out";
const LANGUAGE_ID = "language";

@Component({
  tag: "gx-ide-current-user-info",
  styleUrl: "current-user-info.scss",
  shadow: true,
  assetsDirs: ["gx-ide-assets/current-user-info"]
})
export class GxIdeNewEnvironment {
  #componentLocale: any;
  #initials: string = "";
  // #mode: Mode;
  #menuDropdownModel: ActionMenuModel;

  @Element() el: HTMLGxIdeCurrentUserInfoElement;
  #userLoginButton: HTMLButtonElement;

  @State() showMenu: boolean = false;

  /**
   * User data
   */
  @Prop() readonly userInfo: UserInfo;

  /**
   * Callback executed when the user click "My Account" link
   */
  @Prop() readonly myAccountCallback!: () => Promise<void>;

  /**
   * Callback executed when the user click "change team" link
   */
  @Prop() readonly changeTeamCallback!: () => Promise<void>;

  /**
   * Callback executed when the user click "Language" link
   */
  @Prop() readonly changeLanguageCallback!: () => Promise<void>;

  /**
   * Callback executed when the user switch the "Mode" status
   */
  @Prop() readonly changeModeCallback!: (mode: Mode) => Promise<void>;

  /**
   * Defines the menu block and inline alignment
   */
  @Prop() readonly menuAlign: MenuAlign;

  /**
   * Callback executed when the user click "Sign Out" link
   */
  @Prop() readonly signOutCallback!: () => Promise<void>;

  /**
   * Callback executed when the user clicks on the "term and conditions" link
   */
  @Prop() readonly termsCallback: () => Promise<void>;

  /**
   * Callback executed when the user clicks on the "privacy policy" link
   */
  @Prop() readonly privacyCallback: () => Promise<void>;

  async componentWillLoad() {
    this.#componentLocale = await Locale.getComponentStrings(this.el);
    this.#initials = getInitialsFromName(this.userInfo.name);
    this.#menuDropdownModel = [
      // {
      //   id: MY_ACCOUNT_ID,
      //   caption: this.#componentLocale.myAccountLabel,
      //   showSeparator: false
      // },
      {
        id: CHANGE_TEAM_ID,
        caption: this.#componentLocale.changeTeamLabel
      },
      {
        id: SIGN_OUT_ID,
        caption: this.#componentLocale.signOutLabel
      },
      {
        type: "separator"
      },
      {
        type: "slot",
        id: "agreement"
      }
    ];
  }

  // #handleModeSwitch = () => {
  //   this.#mode = this.#mode === "dark" ? "light" : "dark";
  //   if (this.changeModeCallback) {
  //     this.changeModeCallback(this.#mode);
  //   }
  // };

  #dropDownitemClickHandler = (
    event: ChActionMenuRenderCustomEvent<ActionMenuItemActionableModel>
  ): void => {
    const id = event.detail.id;
    if (id === MY_ACCOUNT_ID) {
      // this.myAccountCallback();
    } else if (id === CHANGE_TEAM_ID) {
      this.changeTeamCallback();
    } else if (id === SIGN_OUT_ID) {
      this.signOutCallback();
    } else if (id === LANGUAGE_ID) {
      this.changeLanguageCallback();
    }
  };

  render() {
    const userName = limitFullName(this.userInfo?.name);
    return (
      <Host class="widget">
        <ch-theme model={CSS_BUNDLES}></ch-theme>
        <ch-action-menu-render
          class="dropdown user-login__button"
          model={this.#menuDropdownModel}
          inlineAlign="inside-end"
          blockAlign="outside-end"
          onButtonClick={this.#dropDownitemClickHandler}
        >
          <div class="user-login__details">
            {userName ? (
              <span class="body-semi-bold-s user-login__name">{userName}</span>
            ) : null}
            {this.userInfo?.team ? (
              <span class="body-regular-xs user-login__organization">
                {this.userInfo.team}
              </span>
            ) : null}
          </div>
          <div class="user-login__avatar">{this.#initials}</div>
          <ch-image
            src={CHEVRON_DOWN_ICON}
            class="icon-m"
            containerRef={this.#userLoginButton}
          ></ch-image>
          <div class="user-login__footer footer-slot" slot="agreement">
            <button
              class="body-regular-s agreement-link"
              onClick={this.termsCallback}
            >
              {this.#componentLocale.termsAndConditionsCaption}
            </button>
            <button
              class="body-regular-s	agreement-link"
              onClick={this.privacyCallback}
            >
              {this.#componentLocale.privacyPolicyCaption}
            </button>
          </div>
        </ch-action-menu-render>
      </Host>
    );
  }
}

export type MenuAlign = {
  blockAlign: ChPopoverAlign;
  inlineAlign: ChPopoverAlign;
};

export type UserInfo = {
  name: string;
  team: string;
};

export type Mode = "dark" | "light";
